import BeelditSwitchInput from "@beeldit/core/components/BeelditSwitchInput";
import { RowAction } from "@beeldit/core/enums/RowAction.enum";
import { TableAction } from "@beeldit/core/enums/TableAction.enum";
import TreatmentSelector from "@main/treatments/components/TreatmentSelector";
import { Gear, Pencil, Plus, Trash } from "@phosphor-icons/react";
import { t } from "i18next";
import { min } from "lodash";

const config = {
  modelName: "customer",
  modelPluralName: "customers",
  endpoint: "customers",
  detailsFunctionPath: "/panel/1/1/customers/",
  indexQueryParams: {
    relations: ["treatments"],
  },
  tableConfig: {
    index: null,
    actions: [
      {
        name: TableAction.CREATE,
        label: "create",
        showLabel: true,
        allowed: true,
        show: true,
        action: null,
        icon: <Plus size={20} />,
        roles: ["admin", "superadmin"],
      },
    ],
    rowActions: [
      {
        name: RowAction.EDIT,
        label: "edit",
        icon: <Pencil size={20} />,
        allowed: true,
        show: true,
        action: (element: any) => {
          console.log("edit", element);
        },
      },
      {
        name: RowAction.DELETE,
        label: "delete",
        icon: <Trash size={20} />,
        allowed: true,
        show: true,
        roles: ["admin", "superadmin"],
        action: (element: any) => {
          console.log("delete", element);
        },
      },
    ],
    rowConfiguration: {
      class: (element: any) => {
        return null;
      },
    },
    columns: [
      {
        name: "id",
        label: "#",
        key: "id",
        type: "number",
        show: true,
        allowOrder: true,
      },
      {
        name: "full_name",
        label: "full_name",
        key: "full_name",
        type: "string",
        show: true,
        allowOrder: true,
      },
      {
        name: "birthday",
        label: "birthday",
        key: "birthday",
        type: "date",
        show: true,
        allowOrder: true,
      },
      {
        name: "phone",
        label: "phone",
        key: "phone",
        type: "string",
        show: true,
        allowOrder: true,
        roles: ["admin", "superadmin"],
      },
      {
        name: "email",
        label: "email",
        key: "email",
        type: "string",
        show: true,
        allowOrder: true,
        roles: ["admin", "superadmin"],
      },
      {
        name: "status",
        label: "status",
        key: "status",
        type: "boolean",
        show: true,
        allowOrder: true,
      },
      {
        name: "treatments",
        label: "treatments",
        key: "treatments",
        attribute: "name",
        type: "array",
        show: true,
        allowOrder: true,
      },
    ],
    pagination: {
      enabled: true,
      back: true,
    },
  },
  filterConfig: {
    type: "object",
    properties: {
      search: {
        type: "string",
      },
      status: {
        type: ["boolean", "null"],
        title: t("customer_status"),
        default: null,
        oneOf: [
          {
            const: null,
            title: "Cualquier estado",
          },
          {
            const: "false",
            title: "Inactivo",
          },
          {
            const: 1,
            title: "Activo",
          },
        ],
      },
      treatment_id: {
        type: "number",
        title: t("treatment_id"),
      },
      origin: {
        type: "number",
        title: t("origin"),
        default: null,
        oneOf: [
          {
            const: null,
            title: "Todas las fuentes",
          },
          {
            const: 1,
            title: "Antiguo paciente",
          },
          {
            const: 2,
            title: "Derivación profesional",
          },
          {
            const: 3,
            title: "Redes sociales",
          },
          {
            const: 4,
            title: "Web",
          },
          {
            const: 5,
            title: "Recomendación de un amigo/familiar",
          },
          {
            const: 6,
            title: "Otros",
          },
        ],
      },
    },
  },
  filterUIConfig: {
    uiScheme: {
      className: "filters-container",
      search: {
        "ui:options": {
          label: false, // Oculta el título del campo
          placeholder: "Buscar...",
        },
      },
      status: {
        "ui:widget": "select",
        "ui:options": {
          label: false,
        },
      },
      treatment_id: {
        "ui:widget": TreatmentSelector,
        "ui:options": {
          label: false,
        },
      },
      origin: {
        "ui:options": {
          label: false,
        },
      },
    },
    widgets: {},
  },
  formSchema: {
    type: "object",
    properties: {
      // Name
      name: {
        type: ["string", "null"],
        title: t("name"),
      },
      // Surnames
      first_lastname: {
        type: ["string", "null"],
        title: t("first_lastname"),
      },

      birthday: {
        type: ["string", "null"],
        title: t("birthdate"),
        format: "date",
      },
      // Phone
      phone: {
        type: ["string", "null"],
        title: t("phone"),
      },

      // Email
      email: {
        type: ["string"],
        title: t("email"),
      },
      // NIF
      nif: {
        type: ["string", "null"],
        title: t("nif"),
      },
      profession: {
        type: ["string", "null"],
        title: t("profession"),
      },
      // Address
      address: {
        type: ["string", "null"],
        title: t("address"),
      },
      // Postal code
      postal_code: {
        type: ["string", "null"],
        title: t("postal_code"),
      },
      origin: {
        type: ["number", "null"],
        title: t("origin"),
        default: 1,
        oneOf: [
          {
            const: -1,
            title: "Seleccione una opción",
          },
          {
            const: 1,
            title: "Antiguo paciente",
          },
          {
            const: 2,
            title: "Derivación profesional",
          },
          {
            const: 3,
            title: "Redes sociales",
          },
          {
            const: 4,
            title: "Web",
          },
          {
            const: 5,
            title: "Recomendación de un amigo/familiar",
          },
          {
            const: 6,
            title: "Otros",
          },
        ],
      },
      status: {
        type: ["boolean", "number", "null"],
        title: t("customer_status"),
      },
      minor: {
        type: ["boolean", "number", "null"],
        title: t("minor"),
      },
      tutor_1_name: {
        type: ["string", "null"],
        title: t("tutor_1_name"),
      },
      tutor_1_nif: {
        type: ["string", "null"],
        title: t("tutor_1_nif"),
      },
      tutor_2_name: {
        type: ["string", "null"],
        title: t("tutor_1_name"),
      },
      tutor_2_nif: {
        type: ["string", "null"],
        title: t("tutor_2_nif"),
      },
    },
  },
  uiFormSchema: {
    "ui:submitButtonOptions": {
      props: {
        disabled: false,
        className: "btn btn-info",
      },
      norender: true,
    },
    status: {
      "ui:widget": BeelditSwitchInput,
    },
    minor: {
      "ui:widget": BeelditSwitchInput,
    },
    colsSchema: [
      {
        cols: "col-12",
        elements: [
          [
            {
              name: "name",
              cols: "col-6",
            },
            {
              name: "first_lastname",
              cols: "col-6",
            },
            {
              name: "birthday",
              cols: "col-6",
            },
            {
              name: "profession",
              cols: "col-6",
            },
            {
              name: "address",
              cols: "col-6",
            },
            {
              name: "postal_code",
              cols: "col-6",
            },
            {
              name: "email",
              cols: "col-6",
            },
            {
              name: "phone",
              cols: "col-6",
            },
            {
              name: "nif",
              cols: "col-6",
            },
            {
              name: "origin",
              cols: "col-6",
            },
            {
              name: "status",
              cols: "col-6",
            },
            {
              name: "minor",
              cols: "col-6",
            },
            {
              name: "tutor_1_name",
              cols: "col-6",
            },
            {
              name: "tutor_1_nif",
              cols: "col-6",
            },
            {
              name: "tutor_2_name",
              cols: "col-6",
            },
            {
              name: "tutor_2_nif",
              cols: "col-6",
            },
          ],
        ],
      },
    ],
  },
};
export default config;
